import React, { useState } from "react";

import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";

import { listBranches } from "@domains/application/module/api/applicationDeployApi";
import type { Application } from "src/domains/application/types";
import { getStatusChipProps } from "src/shell/styles/themeUtils";

// ── Deploy mode labels ───────────────────────────────────────────────────────

const PIPELINE_MODE_LABELS: Record<string, string> = {
  rusha_managed: "Rusha Managed",
  ci_build_rusha_deploy: "CI Build",
};

// ── Types ────────────────────────────────────────────────────────────────────

interface StackPageHeaderProps {
  system: Application;
  /** Primary action: Release (build + deploy) */
  onRunPipeline: () => void;
  /** Deploy only (skip build) */
  onDeploy: () => void;
  /** Promote to another environment */
  onPromote: (slug: string) => void;
  /** Edit configuration */
  onEdit: () => void;
  /** Open delete dialog */
  onDelete: () => void;
  /** Whether the primary action is loading */
  actionLoading: boolean;
  /** Whether deploy is available */
  canDeploy: boolean;
  /** Promotion targets */
  promotionTargets: { slug: string; name: string }[];
  /** Explicit environment targets for manual runs. */
  targetEnvironments: { slug: string; name: string }[];
  selectedTargetEnvironment: string;
  onTargetEnvironmentChange: (slug: string) => void;
  /** Selected branch for manual runs (trunk-based; defaults to main). */
  selectedBranch: string;
  onBranchChange: (branch: string) => void;
  /** Whether stack is adopted (edit goes to services review) */
  isAdopted: boolean;
}

// ── Component ────────────────────────────────────────────────────────────────

/**
 * StackPageHeader — simple page header matching the mockup:
 * title + status chips + action buttons. No separate status bar.
 */
export function StackPageHeader({
  system,
  onRunPipeline,
  onDeploy,
  onPromote,
  onEdit,
  onDelete,
  actionLoading,
  canDeploy,
  promotionTargets,
  targetEnvironments,
  selectedTargetEnvironment,
  onTargetEnvironmentChange,
  selectedBranch,
  onBranchChange,
  isAdopted,
}: StackPageHeaderProps): React.ReactElement {
  const [moreAnchor, setMoreAnchor] = useState<null | HTMLElement>(null);
  const [promoteAnchor, setPromoteAnchor] = useState<null | HTMLElement>(null);
  const [branches, setBranches] = useState<string[]>(["main"]);
  const [branchesLoading, setBranchesLoading] = useState(false);

  const statusChip = getStatusChipProps(system.status);
  const pipelineModeLabel =
    PIPELINE_MODE_LABELS[system.pipelineMode ?? ""] ?? "Rusha Managed";

  return (
    <Box sx={{ mb: 3 }}>
      {/* Title row */}
      <Stack
        direction="row"
        alignItems="center"
        gap={1.5}
        sx={{ mb: 1.5, flexWrap: "wrap" }}
      >
        <Typography
          variant="h4"
          sx={{
            fontSize: 24,
            fontWeight: 700,
            color: "text.primary",
            m: 0,
          }}
        >
          {system.name}
        </Typography>

        <Chip
          label={statusChip.label}
          color={statusChip.color}
          variant={statusChip.variant}
          size="small"
          sx={{ height: 24, fontSize: 12, fontWeight: 600 }}
        />

        <Chip
          label={pipelineModeLabel}
          size="small"
          variant="outlined"
          sx={{ height: 24, fontSize: 12, fontWeight: 600 }}
        />

        {system.vcsRepoUrl && (
          <Chip
            icon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
            label={system.vcsRepoUrl.replace(/^https?:\/\/github\.com\//, "")}
            component="a"
            href={system.vcsRepoUrl}
            target="_blank"
            rel="noopener noreferrer"
            clickable
            size="small"
            variant="outlined"
            sx={{
              height: 24,
              fontSize: 12,
              fontWeight: 600,
              fontFamily: "monospace",
            }}
          />
        )}
      </Stack>

      {/* Action buttons */}
      <Stack
        direction="row"
        gap={1}
        sx={{ flexWrap: "wrap", alignItems: "center" }}
      >
        <FormControl size="small" sx={{ minWidth: 170 }}>
          <InputLabel id="manual-deploy-target-label">Environment</InputLabel>
          <Select
            labelId="manual-deploy-target-label"
            value={selectedTargetEnvironment}
            label="Environment"
            onChange={(event) => onTargetEnvironmentChange(event.target.value)}
            disabled={actionLoading || targetEnvironments.length === 0}
            sx={{ fontSize: 13, borderRadius: "8px" }}
          >
            {targetEnvironments.map((target) => (
              <MenuItem key={target.slug} value={target.slug}>
                {target.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel id="manual-run-branch-label">Branch</InputLabel>
          <Select
            labelId="manual-run-branch-label"
            value={selectedBranch}
            label="Branch"
            onChange={(event) => onBranchChange(event.target.value)}
            onOpen={async () => {
              if (branches.length <= 1 && !branchesLoading) {
                setBranchesLoading(true);
                const result = await listBranches(system.alias);
                if (result.branches.length > 0) {
                  setBranches(result.branches);
                }
                setBranchesLoading(false);
              }
            }}
            disabled={actionLoading}
            sx={{ fontSize: 13, borderRadius: "8px", fontFamily: "monospace" }}
          >
            {branchesLoading ? (
              <MenuItem disabled>Loading…</MenuItem>
            ) : (
              branches.map((b) => (
                <MenuItem key={b} value={b} sx={{ fontFamily: "monospace" }}>
                  {b}
                </MenuItem>
              ))
            )}
          </Select>
        </FormControl>

        <Button
          variant="contained"
          size="small"
          startIcon={
            actionLoading ? (
              <CircularProgress size={14} color="inherit" />
            ) : (
              <PlayArrowIcon sx={{ fontSize: 16 }} />
            )
          }
          onClick={onRunPipeline}
          disabled={actionLoading || !selectedTargetEnvironment}
          sx={{
            textTransform: "none",
            borderRadius: "8px",
            fontSize: 13,
            fontWeight: 500,
            py: 0.75,
            px: 1.75,
          }}
        >
          Release
        </Button>

        {canDeploy && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<RocketLaunchIcon sx={{ fontSize: 16 }} />}
            onClick={onDeploy}
            disabled={actionLoading || !selectedTargetEnvironment}
            sx={{
              textTransform: "none",
              borderRadius: "8px",
              fontSize: 13,
              fontWeight: 500,
              py: 0.75,
              px: 1.75,
            }}
          >
            Deploy
          </Button>
        )}

        {promotionTargets.length > 0 && (
          <>
            <Button
              variant="outlined"
              size="small"
              onClick={(e) => setPromoteAnchor(e.currentTarget)}
              disabled={actionLoading}
              sx={{
                textTransform: "none",
                borderRadius: "8px",
                fontSize: 13,
                fontWeight: 500,
                py: 0.75,
                px: 1.75,
              }}
            >
              Promote
            </Button>
            <Menu
              anchorEl={promoteAnchor}
              open={Boolean(promoteAnchor)}
              onClose={() => setPromoteAnchor(null)}
            >
              {promotionTargets.map((target) => (
                <MenuItem
                  key={target.slug}
                  onClick={() => {
                    setPromoteAnchor(null);
                    onPromote(target.slug);
                  }}
                >
                  {target.name}
                </MenuItem>
              ))}
            </Menu>
          </>
        )}

        <Button
          variant="outlined"
          size="small"
          startIcon={<MoreHorizIcon />}
          onClick={(e) => setMoreAnchor(e.currentTarget)}
          sx={{
            textTransform: "none",
            borderRadius: "8px",
            fontSize: 13,
            fontWeight: 500,
            py: 0.75,
            px: 1.75,
          }}
        >
          More
        </Button>
        <Menu
          anchorEl={moreAnchor}
          open={Boolean(moreAnchor)}
          onClose={() => setMoreAnchor(null)}
        >
          <MenuItem
            onClick={() => {
              setMoreAnchor(null);
              onEdit();
            }}
          >
            {isAdopted ? "Review services" : "Edit configuration"}
          </MenuItem>
          <MenuItem
            onClick={() => {
              setMoreAnchor(null);
              onDelete();
            }}
            sx={{ color: "error.main" }}
          >
            Delete stack
          </MenuItem>
        </Menu>
      </Stack>
    </Box>
  );
}

export default StackPageHeader;
